import { isObject } from '@frontend/utils';

import { CUSTOM_DRAGGABLE_ITEM_TYPES } from './const';
import type {
  CustomDraggableItem,
  CustomDraggableItemType,
  NativeFileBundle,
} from './types';

export const getIsCustomDraggableItem = (
  item: unknown,
): item is CustomDraggableItem => {
  return (
    isObject(item) &&
    typeof item.draggableItemType === 'string' &&
    Object.values(CUSTOM_DRAGGABLE_ITEM_TYPES).includes(
      item.draggableItemType as CustomDraggableItemType,
    )
  );
};

export const getFileSystemEntryFromDataTransferItem = (
  possibleDataTransferItem?: {
    webkitGetAsEntry?: () => FileSystemEntry | null;
    getAsEntry?: () => FileSystemEntry | null;
  } | null,
): FileSystemEntry | null => {
  return (
    possibleDataTransferItem?.webkitGetAsEntry?.() ??
    possibleDataTransferItem?.getAsEntry?.() ??
    null
  );
};

type AddReasonToErrorMessageParam = {
  baseMessage: string;
  causedByError: unknown;
};

const addCausedByToErrorMessage = ({
  baseMessage,
  causedByError,
}: AddReasonToErrorMessageParam): string => {
  const reason =
    isObject(causedByError) &&
    'message' in causedByError &&
    typeof causedByError.message === 'string'
      ? causedByError.message
      : typeof causedByError === 'string'
      ? causedByError
      : null;

  return `${baseMessage}${reason ? ` (caused by: ${reason})` : ''}`;
};

/**
 * Files are excluded from processing if they match any of these patterns.
 *
 * This list was created by combining different lists of common OS-generated
 * files, such as @see https://gist.github.com/whophil/85b1e44a79e7f2e29c5d
 */
const EXCLUDED_FILENAME_REGEXES = [
  // Windows OS-generated files
  /^Thumbs\.db$/,
  /^ehthumbs.*\.db$/,
  /^desktop\.ini$/,
  // MacOS OS-generated files
  /^\.DS_Store?$/,
  /^\..*\.sw[op]$/,
  /^\.Spotlight-V100$/,
  /^\.Trashes$/,
  /^\._.*$/,
] as const satisfies readonly RegExp[];

export const getIsExcludedFilename = (filename: string): boolean =>
  EXCLUDED_FILENAME_REGEXES.some((regEx) => regEx.test(filename));

/**
 * Checks whether we should exclude a file from processing.
 *
 * Currently, we exclude a set list of OS-generated files.
 */
export const getIsExcludedFile = (file: File): boolean =>
  getIsExcludedFilename(file.name);

export const getIsFileSystemFileEntry = (
  fileSystemEntry: FileSystemEntry,
): fileSystemEntry is FileSystemFileEntry => fileSystemEntry.isFile;

export const getIsFileSystemDirectoryEntry = (
  fileSystemEntry: FileSystemEntry,
): fileSystemEntry is FileSystemDirectoryEntry => fileSystemEntry.isDirectory;

/**
 * Gets a File from a FileSystemFileEntry.
 *
 * @throws Throws an error if unable to get File.
 */
export const getFileFromFileSystemFileEntry = async (
  fileSystemFileEntry: FileSystemFileEntry,
): Promise<File> => {
  try {
    const file = await new Promise(
      (resolve: FileCallback, reject: ErrorCallback) =>
        fileSystemFileEntry.file(resolve, reject),
    );

    return file;
  } catch (possibleError) {
    throw new Error(
      addCausedByToErrorMessage({
        baseMessage:
          'Error: Unable to get File from FileSystemFileEntry entry in getFileFromFileSystemFileEntry',
        causedByError: possibleError,
      }),
    );
  }
};

/**
 * Gets FileSystemEntrys for all files/directories in a FileSystemDirectoryEntry.
 *
 * @throws Throws an error if unable to read entries.
 */
export const getAllEntriesFromFileSystemDirectoryEntry = async (
  fileSystemDirectoryEntry: FileSystemDirectoryEntry,
): Promise<FileSystemEntry[]> => {
  try {
    const directoryReader = fileSystemDirectoryEntry.createReader();
    let allEntries: FileSystemEntry[] = [];

    const readEntriesBatch = (): Promise<FileSystemEntry[]> => {
      return new Promise((resolve, reject) => {
        directoryReader.readEntries((entries: FileSystemEntry[]) => {
          if (entries.length) {
            allEntries = allEntries.concat(entries);
            readEntriesBatch().then(resolve).catch(reject);
          } else {
            resolve(allEntries);
          }
        }, reject);
      });
    };

    await readEntriesBatch();
    return allEntries;
  } catch (possibleError) {
    throw new Error(
      addCausedByToErrorMessage({
        baseMessage:
          'Error: Unable to read entries from FileSystemDirectoryEntry in getAllEntriesFromFileSystemDirectoryEntry',
        causedByError: possibleError,
      }),
    );
  }
};

/**
 * Gets all files from a FileSystemEntry, including files in directories.
 *
 * @throws Throws an error if unable to get all files.
 */
export const getAllFilesFromFileSystemEntry = async (
  fileSystemEntry: FileSystemEntry,
): Promise<File[]> => {
  let files: File[] = [];

  const isFileSystemFileEntry = getIsFileSystemFileEntry(fileSystemEntry);

  const isFileSystemDirectoryEntry =
    getIsFileSystemDirectoryEntry(fileSystemEntry);

  if (isFileSystemFileEntry) {
    const fileFromEntry = await getFileFromFileSystemFileEntry(fileSystemEntry);

    const isExcluded = getIsExcludedFile(fileFromEntry);

    if (!isExcluded) {
      files.push(fileFromEntry);
    }
  }

  if (isFileSystemDirectoryEntry) {
    const entries = await getAllEntriesFromFileSystemDirectoryEntry(
      fileSystemEntry,
    );

    const filesFromEntries = (
      await Promise.all(
        entries.map((entry) => getAllFilesFromFileSystemEntry(entry)),
      )
    ).flat();

    const filesToAdd = filesFromEntries.filter(
      (file) => !getIsExcludedFile(file),
    );

    if (filesToAdd.length > 0) {
      files = files.concat(filesToAdd);
    }
  }

  return files;
};

/**
 * Gets all files from a DataTransferItem of kind "file".
 *
 * @throws Throws an error if unable to extract files (including if the DataTransferItem is not of kind "file").
 */
export const getAllFilesFromFileDataTransferItem = async (
  dataTransferItem: DataTransferItem,
): Promise<File[]> => {
  const fileSystemEntry =
    getFileSystemEntryFromDataTransferItem(dataTransferItem);

  if (!fileSystemEntry) {
    throw new Error(
      'Error: Unable to extract files from DataTransferItem in getAllFilesFromFileDataTransferItem: Unable to get FileSystemEntry',
    );
  }

  try {
    const allFiles = await getAllFilesFromFileSystemEntry(fileSystemEntry);

    return allFiles;
  } catch (possibleError) {
    throw new Error(
      addCausedByToErrorMessage({
        baseMessage:
          'Error: Unable to extract files from DataTransferItem in getAllFilesFromFileDataTransferItem',
        causedByError: possibleError,
      }),
    );
  }
};

/**
 * Gets all files from a NativeFileBundle, including files in directories.
 *
 * @throws Throws an error if unable to extract files.
 */
export const getAllFilesFromNativeFileBundle = async (
  nativeFileBundle: NativeFileBundle,
): Promise<File[]> => {
  const { items: dataTransferItemList } = nativeFileBundle;
  if (dataTransferItemList.length === 0) {
    throw new Error(
      'Error: Unable to extract files from nativeFileBundle in getAllFilesFromNativeFileBundle: No DataTransferItems in DataTransferItemList',
    );
  }

  const firstDataTransferItem = dataTransferItemList[0];
  const firstDataTransferItemKind = firstDataTransferItem.kind;

  if (firstDataTransferItemKind === 'file') {
    try {
      return (
        await Promise.all(
          [...dataTransferItemList].map(getAllFilesFromFileDataTransferItem),
        )
      ).flat();
    } catch (possibleError) {
      throw new Error(
        addCausedByToErrorMessage({
          baseMessage:
            'Error: Unable to extract files from nativeFileBundle in getAllFilesFromNativeFileBundle',
          causedByError: possibleError,
        }),
      );
    }
  }

  if (firstDataTransferItemKind === 'string') {
    const allFiles = nativeFileBundle.files;

    return allFiles;
  }

  throw new Error(
    `Error: Unable to extract files from nativeFileBundle in getAllFilesFromNativeFileBundle: Unhandled DataTransferItem.kind: ${firstDataTransferItemKind}`,
  );
};
